<template>
  <div class="page-content almacen-page">
    <div class="almacen-wrapper">
      <div class="almacen-card">
        <form @submit.prevent="guardarOrden">
          <!-- Header -->
          <header class="entrada-header">
            <div>
              <p class="almacen-title">Nueva orden de entrega</p>
              <p class="almacen-subtitle">
                Selecciona el centro, la fecha y las unidades a entregar. Solo se pueden usar unidades en almacén.
              </p>
            </div>

            <div class="entrada-header__actions">
              <button
                type="button"
                class="ios-ghost-button"
                @click="volverListado"
              >
                ← Volver al listado
              </button>
              <button
                type="submit"
                class="ios-primary-button"
                :disabled="guardando || !items.length"
              >
                {{ guardando ? 'Guardando...' : 'Guardar orden' }}
              </button>
            </div>
          </header>

          <!-- Encabezado -->
          <section class="entrada-meta">
            <div class="entrada-meta__row">
              <div class="entrada-field entrada-field--grow entrada-field--centro">
                <label class="entrada-label">Centro de activación</label>
                <div
                  class="centro-picker"
                  @focusin="abrirCentroDropdown"
                  @focusout="programarCierreCentroDropdown"
                >
                  <input
                    v-model.trim="centroBusqueda"
                    type="text"
                    class="entrada-input centro-picker__input"
                    placeholder="Busca por nombre, dirección, # tienda o retailer"
                    autocomplete="off"
                    @input="onCentroBusquedaInput"
                    @keydown.esc="dropdownCentrosAbierto = false"
                  />

                  <button
                    v-if="encabezado.id_centro || centroBusqueda"
                    type="button"
                    class="centro-picker__clear"
                    @mousedown.prevent
                    @click="limpiarCentroSeleccion"
                  >
                    ✕
                  </button>

                  <div
                    v-if="dropdownCentrosAbierto"
                    class="centro-picker__dropdown"
                  >
                    <div
                      v-if="!centrosFiltrados.length"
                      class="centro-picker__empty"
                    >
                      No se encontraron centros con ese filtro.
                    </div>

                    <button
                      v-for="centro in centrosFiltrados"
                      :key="centro.id_centro"
                      type="button"
                      class="centro-picker__option"
                      :class="{ 'centro-picker__option--active': String(encabezado.id_centro) === String(centro.id_centro) }"
                      @mousedown.prevent="seleccionarCentro(centro)"
                    >
                      <div class="centro-picker__option-title">
                        {{ centro.nombre || 'Sin nombre' }}
                      </div>
                      <div class="centro-picker__option-meta">
                        {{ centroEtiquetaSecundaria(centro) || 'Sin datos complementarios' }}
                      </div>
                    </button>
                  </div>
                </div>

                <div
                  v-if="centroSeleccionado"
                  class="centro-picker__selected"
                >
                  <span class="centro-picker__selected-title">{{ centroSeleccionado.nombre }}</span>
                  <span class="centro-picker__selected-meta">{{ centroEtiquetaSecundaria(centroSeleccionado) }}</span>
                </div>
              </div>

              <div class="entrada-field">
                <label class="entrada-label">Fecha programada</label>
                <input
                  type="date"
                  v-model="encabezado.fecha_programada"
                  class="entrada-input"
                />
              </div>
            </div>

            <div class="entrada-meta__row">
              <div class="entrada-field entrada-field--grow">
                <label class="entrada-label">Contacto</label>
                <input
                  type="text"
                  v-model="encabezado.contacto_nombre"
                  class="entrada-input"
                  placeholder="Nombre de la persona que recibe"
                />
              </div>

              <div class="entrada-field">
                <label class="entrada-label">Teléfono</label>
                <input
                  type="text"
                  v-model="encabezado.contacto_telefono"
                  class="entrada-input"
                  placeholder="Teléfono de contacto"
                />
              </div>

              <div class="entrada-field">
                <label class="entrada-label">Correo</label>
                <input
                  type="email"
                  v-model="encabezado.contacto_email"
                  class="entrada-input"
                  placeholder="Correo de contacto"
                />
              </div>
            </div>

            <div class="entrada-meta__row">
              <div class="entrada-field entrada-field--grow">
                <label class="entrada-label">Observaciones generales</label>
                <textarea
                  v-model="encabezado.observaciones"
                  class="entrada-input entrada-input--textarea"
                  rows="2"
                  placeholder="Notas generales de esta orden de entrega..."
                ></textarea>
              </div>
            </div>
          </section>

          <!-- Selección de productos / unidades -->
          <section class="entrada-items">
            <div class="entrada-item-card entrada-item-card--selector">
              <div class="entrada-item-header">
                <span class="entrada-item-badge">
                  Agregar unidades a la orden
                </span>
              </div>

              <div class="entrada-item-row">
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Producto</label>
                  <select
                    v-model="productoSeleccionadoId"
                    class="entrada-input"
                    @change="onProductoChange"
                  >
                    <option value="">Seleccione un producto...</option>
                    <option
                      v-for="p in productos"
                      :key="p.id_producto"
                      :value="p.id_producto"
                    >
                      {{ p.nombre }} ({{ p.unidades_disponibles }} en almacén)
                    </option>
                  </select>
                </div>

                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Unidad / paquete</label>
                  <select
                    v-model="paqueteSeleccionadoId"
                    class="entrada-input"
                    :disabled="!paquetesDisponibles.length || cargandoPaquetes"
                  >
                    <option value="">
                      {{
                        cargandoPaquetes
                          ? 'Cargando unidades...'
                          : 'Seleccione unidad disponible...'
                      }}
                    </option>
                    <option
                      v-for="p in paquetesDisponibles"
                      :key="p.id_unidad_principal"
                      :value="p.id_unidad_principal"
                    >
                      {{ etiquetaPaquete(p) }}
                    </option>
                  </select>
                </div>

                <div class="entrada-field entrada-field--acciones">
                  <label class="entrada-label">&nbsp;</label>
                  <button
                    type="button"
                    class="ios-primary-button"
                    :disabled="!paqueteSeleccionadoId"
                    @click="agregarItemDesdeSeleccion"
                  >
                    + Añadir a la orden
                  </button>
                </div>
              </div>
            </div>

            <!-- Resumen de items agregados -->
            <div
              v-for="(item, index) in items"
              :key="item.uid"
              class="entrada-item-card"
            >
              <div class="entrada-item-header">
                <span class="entrada-item-badge">
                  Equipo #{{ index + 1 }}
                </span>
                <button
                  type="button"
                  class="entrada-item-remove"
                  @click="eliminarItem(index)"
                >
                  Eliminar
                </button>
              </div>

              <div class="entrada-item-row">
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Producto</label>
                  <div class="entrada-resumen-text">
                    {{ item.producto.nombre }}
                  </div>
                </div>

                <div class="entrada-field">
                  <label class="entrada-label">Serie principal</label>
                  <div class="entrada-resumen-text">
                    {{ item.serie_principal }}
                  </div>
                </div>
              </div>

              <div class="entrada-item-row" v-if="item.complementos.length">
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Complementos</label>
                  <ul class="entrada-complementos-list">
                    <li
                      v-for="c in item.complementos"
                      :key="c.id_unidad"
                    >
                      <strong>{{ c.nombre_producto }}:</strong>
                      <span>{{ c.numero_serie }}</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="entrada-item-row">
                <div class="entrada-field entrada-field--grow">
                  <label class="entrada-label">Observaciones del equipo</label>
                  <textarea
                    v-model="item.observaciones"
                    class="entrada-input entrada-input--textarea"
                    rows="2"
                    placeholder="Notas específicas de este equipo en la orden..."
                  ></textarea>
                </div>
              </div>
            </div>

            <div
              v-if="!items.length"
              class="entrada-hint"
            >
              Agrega al menos una unidad principal (con sus complementos) para crear la orden de entrega.
            </div>
          </section>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DashboardCrearOrdenEntrega',
  data() {
    const hoy = new Date().toISOString().slice(0, 10);

    return {
      encabezado: {
        id_centro: '',
        fecha_programada: hoy,
        contacto_nombre: '',
        contacto_telefono: '',
        contacto_email: '',
        observaciones: '',
      },
      centros: [],
      productos: [],
      productoSeleccionadoId: '',
      paquetesDisponibles: [],
      paqueteSeleccionadoId: '',
      items: [],
      cargandoCatalogos: false,
      cargandoPaquetes: false,
      guardando: false,
      centroBusqueda: '',
      dropdownCentrosAbierto: false,
      centroCloseTimer: null,
    };
  },
  mounted() {
    this.cargarCatalogos();
  },
  computed: {
    userToken() { return this.$store.state.user.token; },
    centroSeleccionado() {
      return (this.centros || []).find((c) => String(c.id_centro) === String(this.encabezado.id_centro)) || null;
    },
    centrosFiltrados() {
      const lista = Array.isArray(this.centros) ? this.centros : [];
      const texto = this.normalizarTexto(this.centroBusqueda || '');
      if (!texto) return lista.slice(0, 25);

      const tokens = texto.split(/\s+/).filter(Boolean);
      return lista
        .filter((c) => {
          const haystack = this.centroBusquedaTexto(c);
          return tokens.every((token) => haystack.includes(token));
        })
        .slice(0, 25);
    },
  },
  methods: {
    normalizarTexto(valor) {
      return String(valor || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    },
    centroBusquedaTexto(centro) {
      return this.normalizarTexto([
        centro?.nombre,
        centro?.direccion,
        centro?.retailer_nombre,
        centro?.numero_tienda,
        centro?.ciudad,
        centro?.estado,
        centro?.pais,
      ]
        .filter(Boolean)
        .join(' '));
    },
    centroEtiquetaPrincipal(centro) {
      return centro?.nombre || '';
    },
    centroEtiquetaSecundaria(centro) {
      const partes = [];
      if (centro?.direccion) partes.push(centro.direccion);
      else {
        const ubicacion = [centro?.ciudad, centro?.estado].filter(Boolean).join(', ');
        if (ubicacion) partes.push(ubicacion);
      }
      if (centro?.numero_tienda) partes.push('#' + centro.numero_tienda);
      if (centro?.retailer_nombre) partes.push(centro.retailer_nombre);
      return partes.join(' · ');
    },
    cancelarCierreCentroDropdown() {
      if (this.centroCloseTimer) {
        clearTimeout(this.centroCloseTimer);
        this.centroCloseTimer = null;
      }
    },
    programarCierreCentroDropdown() {
      this.cancelarCierreCentroDropdown();
      this.centroCloseTimer = setTimeout(() => {
        this.dropdownCentrosAbierto = false;
      }, 140);
    },
    abrirCentroDropdown() {
      this.cancelarCierreCentroDropdown();
      this.dropdownCentrosAbierto = true;
    },
    onCentroBusquedaInput() {
      this.abrirCentroDropdown();
      if (
        this.centroSeleccionado &&
        this.centroBusqueda !== this.centroEtiquetaPrincipal(this.centroSeleccionado)
      ) {
        this.encabezado.id_centro = '';
      }
    },
    sincronizarCentroBusqueda() {
      this.centroBusqueda = this.centroSeleccionado
        ? this.centroEtiquetaPrincipal(this.centroSeleccionado)
        : '';
    },
    seleccionarCentro(centro) {
      this.encabezado.id_centro = String(centro.id_centro);
      this.centroBusqueda = this.centroEtiquetaPrincipal(centro);
      this.dropdownCentrosAbierto = false;
    },
    limpiarCentroSeleccion() {
      this.encabezado.id_centro = '';
      this.centroBusqueda = '';
      this.abrirCentroDropdown();
    },
    async cargarCatalogos() {
      this.cargandoCatalogos = true;
      try {
        const res = await axios.get('/api/almacen/ordenes-entrega/catalogos');

        if (res.data?.status === 'ok') {
          this.centros = Array.isArray(res.data.centros)
            ? res.data.centros
            : [];
          this.productos = Array.isArray(res.data.productos)
            ? res.data.productos
            : [];
          this.sincronizarCentroBusqueda();
        } else {
          this.centros = [];
          this.productos = [];
          this.sincronizarCentroBusqueda();
        }
      } catch (error) {
        console.error('Error al cargar catálogos para orden de entrega:', error);
        this.$toast?.error('No se pudieron cargar los catálogos de entrega.');
        this.centros = [];
        this.productos = [];
        this.sincronizarCentroBusqueda();
      } finally {
        this.cargandoCatalogos = false;
      }
    },

    onProductoChange() {
      this.paquetesDisponibles = [];
      this.paqueteSeleccionadoId = '';
      if (!this.productoSeleccionadoId) return;
      this.cargarPaquetesProducto(this.productoSeleccionadoId);
    },

    async cargarPaquetesProducto(idProducto) {
      this.cargandoPaquetes = true;
      try {
        const res = await axios.get(
          '/api/almacen/ordenes-entrega/unidades-disponibles/' + idProducto
        );

        if (res.data?.status === 'ok') {
          this.paquetesDisponibles = Array.isArray(res.data.paquetes)
            ? res.data.paquetes
            : [];
        } else {
          this.paquetesDisponibles = [];
        }

        if (!this.paquetesDisponibles.length) {
          this.$toast?.info(
            'No hay unidades disponibles en almacén para ese producto.'
          );
        }
      } catch (error) {
        console.error('Error al cargar unidades disponibles:', error);
        this.$toast?.error('No se pudieron cargar las unidades disponibles.');
        this.paquetesDisponibles = [];
      } finally {
        this.cargandoPaquetes = false;
      }
    },

    etiquetaPaquete(p) {
      const compPart =
        p.complementos && p.complementos.length
          ? ` + ${p.complementos.length} complemento(s)`
          : ' (sin complementos)';
      const ubicacion = p.ubicacion_actual ? ` · ${p.ubicacion_actual}` : '';

      return `Serie ${p.serie_principal}${compPart}${ubicacion}`;
    },

    agregarItemDesdeSeleccion() {
      if (!this.paqueteSeleccionadoId) return;

      const paquete = this.paquetesDisponibles.find(
        (p) => Number(p.id_unidad_principal) === Number(this.paqueteSeleccionadoId)
      );

      if (!paquete) {
        this.$toast?.error('No se encontró la unidad seleccionada.');
        return;
      }

      const yaExiste = this.items.some(
        (it) => Number(it.id_unidad_principal) === Number(paquete.id_unidad_principal)
      );

      if (yaExiste) {
        this.$toast?.info('Esa unidad ya está agregada a la orden.');
        return;
      }

      const item = {
        uid: 'item-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
        producto: {
          id_producto: paquete.producto.id_producto,
          nombre: paquete.producto.nombre,
        },
        id_unidad_principal: paquete.id_unidad_principal,
        serie_principal: paquete.serie_principal,
        complementos: (paquete.complementos || []).map((c) => ({
          id_unidad: c.id_unidad,
          numero_serie: c.numero_serie,
          nombre_producto: c.nombre_producto,
        })),
        observaciones: '',
      };

      this.items.push(item);
    },

    eliminarItem(index) {
      this.items.splice(index, 1);
    },

    async guardarOrden() {
      if (!this.items.length) {
        this.$toast?.error('Agrega al menos una unidad a la orden.');
        return;
      }

      if (!this.encabezado.id_centro) {
        this.$toast?.error('Selecciona un centro de activación.');
        return;
      }

      this.guardando = true;

      try {
        const payload = {
          encabezado: this.encabezado,
          items: this.items.map((item) => ({
            id_unidad_principal: item.id_unidad_principal,
            complementos: item.complementos.map((c) => c.id_unidad),
            observaciones: item.observaciones,
          })),
          tokenUser: this.userToken,
        };

        const res = await axios.post(
          '/api/almacen/ordenes-entrega/crear',
          payload
        );

        if (res.data?.status === 'ok') {
          this.$toast?.success('Orden de entrega creada correctamente.');

          // Reset formulario
          const hoy = new Date().toISOString().slice(0, 10);
          this.encabezado = {
            id_centro: '',
            fecha_programada: hoy,
            contacto_nombre: '',
            contacto_telefono: '',
            contacto_email: '',
            observaciones: '',
          };
          this.productoSeleccionadoId = '';
          this.paquetesDisponibles = [];
          this.paqueteSeleccionadoId = '';
          this.items = [];
          this.centroBusqueda = '';

          // Volvemos al listado
          this.$router.push({ name: 'DashboardOrdenesEntrega' });
        } else {
          this.$toast?.error(
            res.data?.message || 'No se pudo crear la orden de entrega.'
          );
        }
      } catch (error) {
        console.error('Error al guardar orden de entrega:', error);
        const msg =
          error.response?.data?.message ||
          'Ocurrió un error al guardar la orden de entrega.';
        this.$toast?.error(msg);
      } finally {
        this.guardando = false;
      }
    },

    volverListado() {
      this.$router.push({ name: 'DashboardOrdenesEntrega' });
    },
  },
};
</script>

<style scoped>
.almacen-page {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1.5rem 1rem;
}

.almacen-wrapper {
  width: 100%;
  max-width: 1100px;
}

.almacen-card {
  border-radius: 1.8rem;
  padding: 1.5rem 1.4rem 1.2rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.35),
      rgba(15, 23, 42, 0.96)
    );
  backdrop-filter: blur(24px);
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(15, 23, 42, 0.85);
}

.almacen-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #e5e7eb;
}

.almacen-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
}

/* Header */
.entrada-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
}

.entrada-header__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

/* Meta */
.entrada-meta {
  border-radius: 1.25rem;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.92);
  padding: 0.7rem 0.7rem 0.6rem;
  margin-bottom: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.entrada-meta__row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

/* Items */
.entrada-items {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.entrada-item-card {
  border-radius: 1.3rem;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.9);
  padding: 0.7rem 0.7rem 0.6rem;
}

.entrada-item-card--selector {
  border-style: dashed;
}

/* Inputs */
.entrada-field {
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
  min-width: 0;
}

.entrada-field--grow {
  flex: 1 1 0;
}

.entrada-field--acciones {
  display: flex;
  flex-direction: column;
}

.entrada-label {
  font-size: 0.78rem;
  color: #cbd5f5;
}

.entrada-input {
  border-radius: 0.8rem;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.9);
  padding: 0.4rem 0.7rem;
  font-size: 0.82rem;
  color: #e5e7eb;
  outline: none;
  transition:
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    background 0.16s ease;
}

.entrada-input--textarea {
  resize: vertical;
}

.entrada-input::placeholder {
  color: #6b7280;
}

.entrada-input:focus {
  border-color: rgba(129, 140, 248, 0.95);
  box-shadow: 0 0 0 1px rgba(129, 140, 248, 0.7);
}

/* Item */
.entrada-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
}

.entrada-item-badge {
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  color: #e5e7eb;
  background: rgba(15, 23, 42, 0.96);
}

.entrada-item-remove {
  border: none;
  background: transparent;
  color: #fca5a5;
  font-size: 0.78rem;
  cursor: pointer;
}

.entrada-item-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 0.4rem;
}

/* Complementos */
.entrada-complementos-list {
  list-style: none;
  padding-left: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.entrada-resumen-text {
  font-size: 0.8rem;
  color: #e5e7eb;
}

/* Hints */
.entrada-hint {
  font-size: 0.78rem;
  color: #94a3b8;
}

/* Botones iOS */
.ios-primary-button,
.ios-ghost-button {
  border-radius: 999px;
  padding: 0.45rem 1rem;
  font-size: 0.8rem;
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.08s ease,
    box-shadow 0.16s ease;
}

.ios-primary-button {
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 0.7),
    rgba(37, 99, 235, 1)
  );
  border-color: rgba(129, 140, 248, 0.9);
  color: #e5e7eb;
  box-shadow: 0 14px 32px rgba(37, 99, 235, 0.7);
}

.ios-primary-button:hover {
  transform: translateY(-0.5px);
  box-shadow: 0 18px 38px rgba(37, 99, 235, 0.9);
}

.ios-ghost-button {
  background: rgba(15, 23, 42, 0.9);
  border-color: rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
}

.ios-ghost-button:hover {
  background: rgba(15, 23, 42, 1);
}

/* Responsive */
@media (max-width: 768px) {
  .entrada-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .entrada-meta__row {
    flex-direction: column;
  }

  .entrada-item-row {
    flex-direction: column;
  }
}
.entrada-field--centro {
  position: relative;
}

.centro-picker {
  position: relative;
}

.centro-picker__input {
  padding-right: 2.4rem;
}

.centro-picker__clear {
  position: absolute;
  top: 50%;
  right: 0.7rem;
  transform: translateY(-50%);
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(15, 23, 42, 0.88);
  color: #cbd5e1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.centro-picker__dropdown {
  position: absolute;
  z-index: 30;
  top: calc(100% + 0.45rem);
  left: 0;
  right: 0;
  max-height: 320px;
  overflow-y: auto;
  padding: 0.45rem;
  border-radius: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.98);
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.55);
}

.centro-picker__dropdown::-webkit-scrollbar {
  width: 8px;
}

.centro-picker__dropdown::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.35);
  border-radius: 999px;
}

.centro-picker__dropdown::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.65);
  border-radius: 999px;
}

.centro-picker__empty {
  padding: 0.8rem 0.9rem;
  color: #94a3b8;
  font-size: 0.8rem;
}

.centro-picker__option {
  width: 100%;
  text-align: left;
  padding: 0.75rem 0.8rem;
  border-radius: 0.85rem;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(2, 6, 23, 0.16);
  color: #e5e7eb;
  cursor: pointer;
  display: grid;
  gap: 0.18rem;
}

.centro-picker__option + .centro-picker__option {
  margin-top: 0.35rem;
}

.centro-picker__option:hover,
.centro-picker__option--active {
  border-color: rgba(96, 165, 250, 0.32);
  background: rgba(30, 41, 59, 0.78);
}

.centro-picker__option-title {
  font-size: 0.84rem;
  font-weight: 700;
  color: #f8fafc;
}

.centro-picker__option-meta {
  font-size: 0.74rem;
  color: #94a3b8;
  line-height: 1.35;
}

.centro-picker__selected {
  margin-top: 0.45rem;
  display: grid;
  gap: 0.12rem;
  padding: 0.55rem 0.75rem;
  border-radius: 0.85rem;
  border: 1px solid rgba(96, 165, 250, 0.22);
  background: rgba(30, 41, 59, 0.58);
}

.centro-picker__selected-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: #e5e7eb;
}

.centro-picker__selected-meta {
  font-size: 0.73rem;
  color: #94a3b8;
  line-height: 1.35;
}

</style>
